import React, { useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import Script from 'next/script'
import { LocationContext } from '../../../context/LocationContext'
import { getMarketByName } from '../../../utils/markets'
import { RequestContext } from '../../../context/RequestContext'
import { setLocalStorageEmail, setLocalStorageLoginToken, setLocalStorageJwtToken, setLocalStorageProfilePic } from '../../../js/localstorage'
import { loginState } from '../../../api/user/login-state'
import { useRouter } from 'next/router'
import { uploadImage } from '../../../api/user/post-api'
import { LoadingCover } from '../../commons/LoadingCover'
import { sendNewRequestEmail, userActivateEmail } from '../../../api/email/email-api'
import { SystemError } from '../../error/SystemError'
import { registerUser } from '../../../api/user/register-api'
import { loginWithGoogle } from '../../../api/user/googleloogin-api'
import axios from 'axios'
import { uploadRequest, createRequestByUser } from '../../../api/request/request-api'
import { computePackagePrice } from '../../../utils/pt-package-price'
import { parseJwt } from '../../../service/parse-jwt'
import { motion } from 'framer-motion'
import { googleCaptchaKey } from '../../../utils/constants'
import { UploadingMediaLoader } from '../../commons/UploadingMediaLoader'
import { ModernInput } from '../../commons/BookingFormComponents'
import { nextRequiredField } from '../../../utils/nextRequiredField'
import { iosLogin } from '../../../api/firebase/firebase'
import { 
    ChevronLeft, User, Mail, CheckCircle, Shield, Star, 
    Calendar, Dumbbell, Target, Clock, Trophy, MapPin
} from 'lucide-react'

// Compact Star rating
const MiniStarRating = ({ rating }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className={`w-4 h-4 ${s <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'}`} />
        ))}
        <span className="ml-1 font-semibold text-white/60">{Number(rating).toFixed(1)}</span>
    </div>
)

// Extract criteria values
const getCriteriaValue = (requestCriteria, questionCode) => {
    if (!requestCriteria || !Array.isArray(requestCriteria)) return null
    for (let i = 0; i < requestCriteria.length; i++) {
        const item = requestCriteria[i]
        if (item && item[questionCode] !== undefined && item[questionCode] !== null) {
            return item[questionCode]
        }
    }
    return null
}

const itemVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            duration: 0.2
        }
    }
};

// Terms Agreement Component - Compact
const TermsAgreement = () => {
    return (
        <motion.div 
            variants={itemVariants}
            className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg"
        >
            <div className="shrink-0 w-5 h-5 rounded bg-green-500 flex items-center justify-center mt-0.5">
                <CheckCircle className="w-3 h-3 text-white" />
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
                By continuing, I agree to the{' '}
                <Link href="/terms" className="text-orange-600 hover:text-orange-700 font-medium" target="_blank" rel="noopener">Terms</Link>
                {' '}and{' '}
                <Link href="/privacy-policy" className="text-orange-600 hover:text-orange-700 font-medium" target="_blank" rel="noopener">Privacy Policy</Link>
            </p>
        </motion.div>
    );
};

export const PtQuickSignIn = ({ requestCategorySettings, goToSection2, goBack = goToSection2, matchedTrainer }) => {
    const router = useRouter()
    const affiliateId = router.query.affiliateId
    const referrerId = router.query.referrerId

    const [businessType, setBusinessType] = useState("")
    const [isGoogleBtnLoading, setIsGoogleBtnLoading] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [userFullName, setUserFullName] = useState("")
    const [userId, setUserId] = useState("")
    const [profilePicUrl, setProfilePicUrl] = useState(undefined)
    const [isNewFbGoogleUser, setIsNewFbGoogleUser] = useState(false)
    const [formValues, setFormValues] = useState({ emailId: "", userFullName: "" })
    // The field to point at next (pulsing ring, no autofocus: focusing opens
    // the phone keyboard over the step before it has been read)
    const nextField = nextRequiredField([
        { name: "userFullName", done: !!formValues.userFullName?.trim() },
        { name: "emailId", done: !!formValues.emailId?.trim() },
    ])
    const [isSubmit, setIsSubmit] = useState(false)
    const [formErrors, setFormErrors] = useState({})
    const [captchaToken, setCaptchaToken] = useState("")
    const [uploadPercent, setUploadPercent] = useState(0)
    const [isUploadingMedia, setIsUploadingMedia] = useState(false)
    const [ctaType, setCtaType] = useState("")

    const [isSystemError, setIsSystemError] = useState(false)
    const [errorContent, setErrorContent] = useState({ header: "", body: "" })

    // Guard so the request submission only fires once per userId, even if
    // memoised callbacks/effects get re-created.
    const hasSubmittedRequestRef = useRef(false)

    const { countryName, countryCd, countryCurrCd, countryTelCd, userLatitude, userLongitude } = useContext(LocationContext)
    const {
        requestType, requestDescription, requestCriteria, userLocation,
        serviceDate, startTime, endTime, serviceCost, filesToUpload,
        setFilesToAttach, setFilesToUpload, originalCost, serviceItems, serviceUnitsCount,
        selectedPackage
    } = useContext(RequestContext)

    // Same pricing rules as PtPlanAndPostalCode (online-booking discount enabled +
    // peak premium + female-trainer premium). Used for both display and payload.
    const packagePrice = useMemo(
        () => (selectedPackage
            ? computePackagePrice(selectedPackage, serviceDate, startTime, requestCriteria, { applyOnlineDiscount: true, countryCd: getMarketByName(countryName)?.countryCd ?? 'sg' })
            : null),
        [selectedPackage, serviceDate, startTime, requestCriteria, countryName]
    )

    // Extract criteria for plan summary
    const fitnessGoal = useMemo(() => getCriteriaValue(requestCriteria, "1"), [requestCriteria])
    const trainingLocation = useMemo(() => getCriteriaValue(requestCriteria, "5"), [requestCriteria])
    const workoutTime = useMemo(() => getCriteriaValue(requestCriteria, "6"), [requestCriteria])

    const trainer = matchedTrainer

    const trainerName = trainer?.userFirstName || "Trainer"
    const trainerFullName = trainer ? `${trainer.userFirstName || ""} ${trainer.userLastName || ""}`.trim() : "Your Trainer"

    // ====== All QuickSignIn logic (copied verbatim) ======

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormValues({ ...formValues, [name]: value })
        if (formErrors[name]) setFormErrors({ ...formErrors, [name]: '' })
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setFormErrors(validateForm(formValues))
        grecaptcha.enterprise.ready(async () => {
            const token = await grecaptcha.enterprise.execute(googleCaptchaKey, { action: 'Register' })
            setCaptchaToken(token)
            setIsSubmit(true)
        })
    }

    const validateForm = useCallback((values) => {
        const errors = {}
        if (!values.emailId) errors.emailId = "Email Id is required for important communications."
        if (!values.emailId.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/)) errors.emailId = "Invalid Email Id."
        if (values.userFullName.length < 2) errors.userFullName = "Your name should be at least 2 characters in length."
        return errors
    }, [])

    const callNativeApp = (email) => {
        return new Promise((resolve) => {
            try {
                const fcmToken = window.iosFCMToken || '';
                if (fcmToken && fcmToken.trim() !== '') {
                    iosLogin(email, fcmToken)
                        .then(() => console.log('IosLogin success'))
                        .catch(err => console.log('IosLogin error:', err));
                }
                if (typeof webkit !== 'undefined' && webkit.messageHandlers && webkit.messageHandlers.callbackHandler) {
                    webkit.messageHandlers.callbackHandler.postMessage(email);
                }
                setTimeout(resolve, 500);
            } catch(err) {
                console.log('callNativeApp error:', err.message);
                resolve();
            }
        });
    }

    const goToHome = useCallback(() => {
        setIsLoading(true)
        router.push("/home").then(() => setIsLoading(false))
    }, [router])

    const requestUploadComplete = useCallback((requestIdInt) => {
        if (businessType === 'lead' && isNewFbGoogleUser) {
            sendNewRequestEmail(userId, requestIdInt, requestType, requestDescription)
        }

        try {
            if(ctaType === "claim") {
                gtag('event', 'conversion', { 'send_to': 'AW-823304067/DthiCPas6oIBEIO_yogD', 'transaction_id': userId })
            } else if(ctaType === "explore") {
                gtag('event', 'conversion', { 'send_to': 'AW-823304067/JbmICMqU-4ocEIO_yogD', 'transaction_id': userId })
            }
        } catch (error) { console.log("Unable to track conversion") }

        setTimeout(() => {
            setIsLoading(true)
            const query = `newRequest=true&newRequestIdInt=${requestIdInt}`
            const extra = referrerId ? `&referrerId=${referrerId}` : affiliateId ? `&affiliateId=${affiliateId}` : ''
            router.push(`/home?${query}${extra}`).then(() => setIsLoading(false))
        }, 1000)
    }, [businessType, isNewFbGoogleUser, userId, requestType, requestDescription, referrerId, affiliateId, router])

    const uploadNewRequest = useCallback(async () => {
        try {
            const isMediaPresent = filesToUpload.length > 0
            const requestIdInt = await uploadRequest(
                requestType, requestDescription, requestCriteria, businessType,
                countryName, countryCurrCd, serviceDate, startTime, endTime, isMediaPresent
            )
            setFilesToAttach([])
            if (requestIdInt > 1) requestUploadComplete(requestIdInt)
            if (requestIdInt === 1) { setIsLoading(false); goToHome() }
            if (requestIdInt === 0) {
                setIsLoading(false)
                setErrorContent({ header: "Upload Request Error", body: "Please try again." })
                setIsSystemError(true)
            }
        } catch (error) {
            console.log(error)
            setErrorContent({ header: "Error submitting request", body: "Please try again." })
            setIsSystemError(true)
            setIsLoading(false)
        }
    }, [requestType, requestDescription, requestCriteria, goToHome, countryCurrCd, businessType,
        countryName, serviceDate, startTime, endTime, filesToUpload, setFilesToAttach, requestUploadComplete])

    const createPackageRequestByUser = useCallback(async () => {
        try {
            // Validate mandatory fields for the package flow
            if (!serviceDate || !startTime || !endTime || !userLocation) {
                setErrorContent({
                    header: "Schedule or address missing",
                    body: "Please choose your preferred date/time and address before continuing."
                })
                setIsSystemError(true)
                setIsLoading(false)
                setTimeout(() => goToSection2(), 4000)
                return
            }

            const computed = packagePrice || computePackagePrice(
                selectedPackage, serviceDate, startTime, requestCriteria, { applyOnlineDiscount: true, countryCd: getMarketByName(countryName)?.countryCd ?? 'sg' }
            )

            const payload = {
                requestDTO: {
                    requestType,
                    requestDescription,
                    businessType: "job",
                    countryName,
                    currencyCd: countryCurrCd,
                    serviceDateS: serviceDate,
                    startTime,
                    endTime,
                    isMediaPresent: false,
                    requestCriteria
                },
                spUserId: trainer?.userId || "",
                totalCost: computed.totalCost,
                packageName: selectedPackage.packageName,
                totalServices: selectedPackage.totalServices,
                packageDuration: selectedPackage.packageDuration,
                packageDurationUnit: selectedPackage.packageDurationUnit,
                perServiceTime: selectedPackage.perServiceTime,
                serviceAddress: userLocation,
                isInstalmentPlan: false,
                instalmentFrequency: null,
                instalmentAmount: null,
                totalInstalments: null,
                subscriptionItem: null,
                isBuddyPackage: false
            }

            const response = await createRequestByUser(payload)
            setFilesToAttach([])

            if (response && response.responseCode === 200 && response.requestEntity?.requestIdInt) {
                requestUploadComplete(response.requestEntity.requestIdInt)
            } else {
                setIsLoading(false)
                setErrorContent({
                    header: "Couldn't create your booking",
                    body: response?.responseMessage || "Please try again."
                })
                setIsSystemError(true)
            }
        } catch (error) {
            console.log(error)
            setErrorContent({ header: "Error submitting request", body: "Please try again." })
            setIsSystemError(true)
            setIsLoading(false)
        }
    }, [requestType, requestDescription, requestCriteria, countryName, countryCurrCd,
        serviceDate, startTime, endTime, userLocation, selectedPackage, packagePrice,
        trainer, setFilesToAttach, requestUploadComplete, goToSection2])

    const getProfilePicFile = useCallback(async () => {
        try {
            const response = await axios.get(profilePicUrl, { responseType: 'blob' })
            await uploadImage(response.data, userId, userFullName, "Profile Picture", "Profile", "Public", "strings", "My new Profile Picture")
        } catch (error) { console.log(error) }
        if (requestType != undefined) {
            if (selectedPackage) createPackageRequestByUser()
            else uploadNewRequest()
        } else {
            goToHome()
        }
    }, [userId, userFullName, profilePicUrl, requestType, selectedPackage,
        uploadNewRequest, createPackageRequestByUser, goToHome])

    const processUserId = useCallback(() => {
        if (isNewFbGoogleUser) {
            getProfilePicFile()
        } else if (requestType != undefined) {
            if (selectedPackage) createPackageRequestByUser()
            else uploadNewRequest()
        } else {
            goToHome()
        }
    }, [isNewFbGoogleUser, requestType, selectedPackage, getProfilePicFile,
        uploadNewRequest, createPackageRequestByUser, goToHome])

    const getUserId = async () => {
        try {
            let loginStateResponse = await loginState()
            setUserId(loginStateResponse.id)
        } catch (error) {
            console.log(error)
            setErrorContent({ header: "Cannot get UserId", body: "Please try again." })
            setIsSystemError(true)
            setIsLoading(false)
        }
    }

    const createAccount = useCallback(async () => {
        if (!captchaToken) return
        try { gtag('event', 'conversion', { 'send_to': 'AW-823304067/3YR1CLWy280DEIO_yogD' }) } catch (e) { }
        setIsLoading(true)
        let registerResponse
        try {
            registerResponse = await registerUser(
                formValues.emailId, "", formValues.userFullName, "", "General",
                countryName, countryCd, countryCurrCd, countryTelCd,
                userLocation, userLatitude, userLongitude, captchaToken
            )
        } catch (error) {
            console.log(error)
            setErrorContent({ header: "User Registration Error", body: "Please try again." })
            setIsSystemError(true); setIsLoading(false); setIsSubmit(false); return
        }
        if (registerResponse.responseCode == 0) {
            setErrorContent({ header: "System Error", body: "Please try again." })
            setIsSystemError(true); setIsLoading(false); setIsSubmit(false); return
        }
        if (registerResponse.responseCode == 2) {
            setErrorContent({ header: "Email id is taken", body: "This email id is already registered! Try with a different email id." })
            setIsSystemError(true); setIsLoading(false); setIsSubmit(false); return
        }
        if (registerResponse.responseCode == 1) {
            setLocalStorageEmail(formValues.emailId)
            setLocalStorageLoginToken(registerResponse.loginToken)
            setLocalStorageJwtToken(registerResponse.jwtToken)
            userActivateEmail(formValues.emailId, formValues.userFullName)
            getUserId()
        }
    }, [formValues, countryName, countryCd, countryCurrCd, countryTelCd, userLocation, userLatitude, userLongitude, captchaToken])

    const loginGoogleUser = useCallback(async (email, name, pic, googleLoginCaptcha) => {
        try { gtag('event', 'conversion', { 'send_to': 'AW-823304067/3YR1CLWy280DEIO_yogD' }) } catch (e) { }
        setIsLoading(true)
        let googleLoginResponse
        try {
            googleLoginResponse = await loginWithGoogle(email, name, pic, countryName, countryCd, countryCurrCd, countryTelCd, userLocation, userLatitude, userLongitude, googleLoginCaptcha)
        } catch {
            setErrorContent({ header: "Google Login Problem", body: "Please try again." })
            setIsSystemError(true); setIsLoading(false); setIsSubmit(false); return
        }
        setLocalStorageEmail(email); setLocalStorageLoginToken(googleLoginResponse.loginToken)
        setLocalStorageJwtToken(googleLoginResponse.jwtToken); setLocalStorageProfilePic(pic)
        setProfilePicUrl(pic); setUserFullName(name)
        if (typeof android != "undefined") android.showToast(email)
        await callNativeApp(email)
        if (googleLoginResponse.responseCode === 1) setIsNewFbGoogleUser(true)
        getUserId()
    }, [countryName, countryCd, countryCurrCd, countryTelCd, userLocation, userLatitude, userLongitude])

    const handleCredentialResponse = useCallback((response) => {
        const decoded = parseJwt(response.credential)
        grecaptcha.enterprise.ready(async () => {
            const token = await grecaptcha.enterprise.execute(googleCaptchaKey, { action: 'GoogleLogin' })
            loginGoogleUser(decoded.email, decoded.name, decoded.picture, token)
        })
    }, [loginGoogleUser])

    const initializeGoogleLogin = useCallback(async () => {
        if (typeof google === 'undefined' || !google?.accounts?.id) {
            return;
        }
        setIsGoogleBtnLoading(true)
        try {
            await google.accounts.id.initialize({
                client_id: "97723445335-nmgmbth2t3t6gqjr1jis91to3gj8vf1s.apps.googleusercontent.com",
                callback: handleCredentialResponse
            })
            await google.accounts.id.renderButton(
                document.getElementById("ptButtonDiv"),
                { theme: "outline", size: "large", width: "280", logo_alignment: "left" }
            )
            setIsGoogleBtnLoading(false)
        } catch (error) { setIsGoogleBtnLoading(false); console.log(error) }
    }, [handleCredentialResponse])

    useEffect(() => {
        if (Object.keys(formErrors).length == 0 && isSubmit) createAccount()
        else setIsSubmit(false)
    }, [formErrors, isSubmit, createAccount])

    useEffect(() => {
        if (!userId) return
        if (hasSubmittedRequestRef.current) return
        hasSubmittedRequestRef.current = true
        processUserId()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId])
    useEffect(() => { initializeGoogleLogin() }, [initializeGoogleLogin])

    useEffect(() => {
        if (!requestCategorySettings || !requestType) return
        for (let x in requestCategorySettings) {
            if (requestType === requestCategorySettings[x].requestType) {
                setBusinessType(requestCategorySettings[x].businessType)
            }
        }
        if (requestCriteria.length === 0) {
            setErrorContent({ header: "Your requirements data expired", body: "Please re-submit the form in 3 secs." })
            setIsSystemError(true)
            setTimeout(() => goToSection2(), 5000)
        }
    }, [requestCategorySettings, requestType, goToSection2, requestCriteria.length])

    return (
        <>
            <div className="px-3">
                <form className="max-w-md mx-auto space-y-3" onSubmit={handleSubmit}>
                    {/* ====== 1) TRAINER CARD (compact) ====== */}
                    {trainer && (
                        <div className='p-2 bg-slate-900 backdrop-blur-md rounded-2xl border border-white/10 relative'>
                            <p className="absolute right-2 text-xs font-semibold text-white/60 uppercase tracking-wide">Best Match</p>
                            <div className="flex items-center gap-3">
                                <div className="relative shrink-0 w-30 h-30 rounded-xl overflow-hidden border-2 border-white/20 shadow">
                                    {trainer.profilePic ? (
                                        <Image src={trainer.profilePic} alt={trainerFullName}
                                            width={100} height={100} className="object-cover w-full h-full" />
                                    ) : (
                                        <div className="w-full h-full bg-linear-to-br from-orange-400 to-amber-400 flex items-center justify-center">
                                            <span className="text-white font-bold text-xl">{trainerName.charAt(0)}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0 space-y-1">
                                    <h4 className="text-sm font-bold text-white truncate">{trainerFullName}</h4>
                                    <MiniStarRating rating={trainer.rating || 5} />
                                    <div className="flex items-center gap-2 mt-5">
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 rounded-full">
                                            <Trophy className="w-3 h-3 text-green-400" />
                                            <span className="text-xs font-semibold text-green-300">Certified</span>
                                        </span>
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 rounded-full">
                                            <Shield className="w-3 h-3 text-blue-400" />
                                            <span className="text-xs font-semibold text-blue-300">Employee</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ====== 2) TRAINING PLAN SUMMARY (compact) ====== */}
                    {/* {timeline && (
                        <div className="p-3 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 space-y-2">
                            <p className="text-xs font-semibold text-white/40 uppercase tracking-wide">Your Training Plan</p>
                            <div className="flex gap-2">
                                <div className="flex-1 flex items-center gap-2 bg-white/5 rounded-lg px-2.5 py-2 border border-white/10">
                                    <Calendar className="w-4 h-4 text-orange-400 shrink-0" />
                                    <div>
                                        <div className="text-sm font-bold text-white">{timeline.weeks.min}-{timeline.weeks.max} wks</div>
                                    </div>
                                </div>
                                <div className="flex-1 flex items-center gap-2 bg-white/5 rounded-lg px-2.5 py-2 border border-white/10">
                                    <Dumbbell className="w-4 h-4 text-orange-400 shrink-0" />
                                    <div>
                                        <div className="text-sm font-bold text-white">{timeline.sessionsPerWeek}x/wk</div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {fitnessGoal && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-500/20 text-orange-300 rounded-full text-xs font-medium">
                                        <Target className="w-2.5 h-2.5" /> {fitnessGoal}
                                    </span>
                                )}
                                {trainingLocation && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full text-xs font-medium">
                                        <MapPin className="w-2.5 h-2.5" /> {trainingLocation}
                                    </span>
                                )}
                                {workoutTime && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-2xs font-medium">
                                        <Clock className="w-2.5 h-2.5" /> {workoutTime}
                                    </span>
                                )}
                            </div>
                        </div>
                    )} */}

                    {/* <div className="flex gap-2 px-3 py-2 bg-green-500/10 rounded-xl border border-green-500/20">
                        <CheckCircle className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-white/60 leading-relaxed">
                            Congratulations! You get a <span className="font-bold text-green-300">trial session</span> and consultation with your trainer.
                        </p>
                    </div> */}

                    {/* ====== 3) FORM FIELDS ====== */}
                    <ModernInput
                        type="text" name="userFullName" placeholder="John Doe"
                        value={formValues.userFullName} onChange={handleChange}
                        icon={User} label="Name" required error={formErrors.userFullName}
                        highlight={nextField === "userFullName"}
                    />
                    <ModernInput
                        type="email" name="emailId" placeholder="john.doe@gmail.com"
                        value={formValues.emailId} onChange={handleChange}
                        icon={Mail} label="Email" required error={formErrors.emailId}
                        highlight={nextField === "emailId"}
                    />
                    <TermsAgreement />

                    {/* Terms */}
                    {/* <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                        <div className="shrink-0 w-5 h-5 rounded bg-green-500 flex items-center justify-center mt-0.5">
                            <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">
                            By continuing, I agree to the{' '}
                            <Link href="/terms" className="text-orange-600 font-medium" target="_blank" rel="noopener">Terms</Link>
                            {' '}and{' '}
                            <Link href="/privacy-policy" className="text-orange-600 font-medium" target="_blank" rel="noopener">Privacy Policy</Link>
                        </p>
                    </div> */}

                    {isUploadingMedia && <UploadingMediaLoader uploadPercent={uploadPercent} />}

                    {/* Submit */}
                    <div className="pt-3 space-y-3">
                        <motion.button type="submit" disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-linear-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-600 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed text-white font-bold text-lg rounded-full shadow-lg shadow-orange-200/50 transition-all duration-300"
                            onClick={() => setCtaType("claim")}
                            whileHover={!isLoading ? { scale: 1.02 } : {}}
                            whileTap={!isLoading ? { scale: 0.98 } : {}}>
                            {isLoading ? (
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <div>
                                    <div>
                                        Continue with {trainerName}
                                    </div>
                                    {/* <div className='text-sm text-orange-100'>
                                        
                                    </div>     */}
                                </div>
                            )}
                        </motion.button>
                        <motion.button
                            type="submit"
                            className="w-full flex items-center justify-center gap-2 px-6 py-3
                                        border border-orange-400 bg-transparent
                                        text-orange-400 font-semibold rounded-full shadow-sm
                                        hover:bg-orange-500/10 transition-all duration-300"
                            onClick={() => setCtaType("explore")}
                            >
                            Explore Other Trainers
                        </motion.button>
                    </div>
                </form>

                {/* Divider */}
                {/* <div className="relative py-4 max-w-md mx-auto">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/10"></div>
                    </div>
                    <div className="relative flex justify-center">
                        <span className="px-4 text-sm text-gray-400">or continue with</span>
                    </div>
                </div> */}

                {/* Google Sign In Button */}
                {/* <div className="flex justify-center py-1 min-h-12">
                    <div id="ptButtonDiv" className="flex justify-center w-70 h-10"></div>
                </div> */}

                {/* Security Badge */}
                {/* <div className="flex items-center justify-center gap-2 mt-2 text-xs text-gray-400">
                    <Shield className="w-4 h-4" />
                    <span>Your information is secure and encrypted</span>
                </div> */}

                {/* Back */}
                <div className="flex justify-center mt-1">
                    <motion.button type="button" onClick={() => goBack()}
                        className="flex items-center gap-2 px-4 py-3 text-gray-400 hover:text-orange-400 rounded-xl hover:bg-white/5 transition-all duration-300"
                        whileHover={{ x: -3 }} whileTap={{ scale: 0.95 }}>
                        <ChevronLeft className="w-5 h-5" /><span className="font-medium">Back</span>
                    </motion.button>
                </div>

                {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError} />}
                {isLoading && <LoadingCover />}
            </div>
            <Script id="google-captcha-pt" src={`https://www.google.com/recaptcha/enterprise.js?render=${googleCaptchaKey}`} strategy="lazyOnload" />
        </>
    )
}
